import {
  type AdminActionRequest,
  type AdminBlackjackOperationsResponse,
  type AdminCaseQueueQuery,
  type AdminConfigProfileActivationRequest,
  type AdminConfigProfileCancellationRequest,
  type AdminPokerOperationsResponse,
  type AdminRouletteOperationsResponse,
  addPlayerCaseNoteRequest,
  adminActionDecision,
  adminActionRequest,
  adminAuditResponse,
  adminBlackjackOperationsResponse,
  adminCaseQueueResponse,
  adminConfigProfileActivationRequest,
  adminConfigProfileActivationResponse,
  adminConfigProfileCancellationRequest,
  adminConfigProfileCancellationResponse,
  adminLoginRequest,
  adminPokerOperationsResponse,
  adminReauthRequest,
  adminRouletteOperationsResponse,
  adminRoundDetailResponse,
  adminRoundsResponse,
  adminSessionResponse,
  adminStatusChangeRequest,
  adminStatusChangeResponse,
  adminTableInstance,
  approvedActionResponse,
  type CreatePlayerCaseRequest,
  casinoEnvironmentDrainStatus,
  casinoEnvironmentLifecycleRequest,
  casinoEnvironmentSummary,
  casinoProfitResponse,
  createPlayerCaseRequest,
  createRoyalSpinCampaignRequest,
  forceLogoutResponse,
  operatorPolicyWriteRequest,
  operatorProfileArchiveRequest,
  operatorProfileLibrary,
  operatorProfileWriteRequest,
  playerCaseResponse,
  playerCasesResponse,
  playerGameRoundDetailResponse,
  playerGameRoundsResponse,
  playerLedgerResponse,
  playerOverviewResponse,
  playerResponsibleGamingResponse,
  playerSearchResponse,
  playerSessionsResponse,
  raisedActionResponse,
  rejectedActionResponse,
  rngChainVerification,
  royalSpinCampaignDecisionRequest,
  royalSpinCampaignDetailResponse,
  royalSpinCampaignLifecycleResponse,
  royalSpinCampaignListResponse,
  royalSpinCampaignVersion,
  royalSpinKillSwitchRequest,
  royalSpinKillSwitchResponse,
  transactionExplorerDetail,
  transactionExplorerResponse,
  type UpdatePlayerCaseRequest,
  updatePlayerCaseRequest,
} from "@utopia-well-done-ltd/contracts";
import { z } from "zod";
import { chatDetail, chatQueue } from "./chat.js";
import {
  type FeedbackFilters,
  feedbackPage,
  feedbackReply,
  feedbackReview,
  feedbackSummary,
} from "./feedback.js";
import { type WaitlistFilters, waitlistPage, waitlistReview } from "./waitlist.js";

/**
 * Where the API lives. Read from the build and never derived from `window.location`: the backoffice
 * and the API are deliberately different origins, and a base URL taken from the page is a base URL
 * that whoever controls the page can move.
 */
const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:3000";

/** Sign-out returns a bare acknowledgement; there is no contract shape worth sharing for it. */
const signOutResponse = z.object({ status: z.string() });
const reauthResponse = z.object({ status: z.literal("reauthenticated") });
const adminRoleValue = z.enum(["support", "kyc_officer", "finance", "admin", "super_admin"]);
const pendingOperatorAction = z.object({
  actionType: z.string().min(1),
  expiresAt: z.iso.datetime(),
  id: z.uuid(),
  initiatedAt: z.iso.datetime(),
  initiatedBy: z.uuid(),
  initiatedByEmail: z.email(),
  justification: z.string(),
  payload: z.unknown(),
  targetId: z.string().min(1),
  targetType: z.string().min(1),
});
const pendingOperatorActionsResponse = z.object({ actions: z.array(pendingOperatorAction) });
export type PendingOperatorAction = z.infer<typeof pendingOperatorAction>;
const operatorActionRequest = z.union([
  adminActionRequest,
  z.object({
    actionType: z.literal("admin.role_change"),
    justification: z.string().trim().min(20),
    payload: z.object({ role: adminRoleValue }),
    targetId: z.uuid(),
  }),
]);
export type OperatorActionRequest = z.infer<typeof operatorActionRequest>;
const operatorPage = z.object({
  operators: z.array(
    z.object({
      createdAt: z.iso.datetime(),
      disabledAt: z.iso.datetime().nullable(),
      email: z.string(),
      id: z.uuid(),
      role: adminRoleValue,
    }),
  ),
});
export type OperatorSummary = z.infer<typeof operatorPage>["operators"][number];
const moderatorGrant = z.object({
  grant: z
    .object({
      active: z.boolean(),
      expiresAt: z.iso.datetime(),
      revokedAt: z.iso.datetime().nullable(),
    })
    .nullable(),
});
const operatorApprovalResponse = z.union([
  approvedActionResponse,
  z.object({
    actionType: z.literal("admin.role_change"),
    adminUserId: z.uuid(),
    payload: z.unknown(),
    status: z.literal("approved"),
    targetId: z.uuid(),
  }),
]);
const roomUser = z.object({
  avatarId: z.string().nullable(),
  avatarLabel: z.string(),
  away: z.boolean(),
  displayName: z.string(),
  position: z.object({ x: z.number(), y: z.number(), z: z.number() }).nullable(),
  reconnecting: z.boolean(),
  seatId: z.string().nullable(),
  sessionId: z.string(),
  tablePlacements: z.object({
    blackjack: z.string().nullable(),
    poker: z.string().nullable(),
    roulette: z.string().nullable(),
  }),
  userId: z.string(),
});
const roomOperationsResponse = z.object({
  generatedAt: z.string().datetime(),
  rooms: z.array(
    z.object({
      acceptingJoins: z.boolean(),
      connectedClients: z.number().int().nonnegative(),
      environmentInstanceId: z.string().nullable(),
      lifecycle: z.string().nullable(),
      maxClients: z.number().int().positive(),
      reconnectingClients: z.number().int().nonnegative(),
      roomId: z.string(),
      sceneId: z.string(),
      users: z.array(roomUser),
      voiceMode: z.string(),
    }),
  ),
});
export type RoomOperationsResponse = z.infer<typeof roomOperationsResponse>;
export type BlackjackOperationsResponse = AdminBlackjackOperationsResponse;
export type PokerOperationsResponse = AdminPokerOperationsResponse;
export type RouletteOperationsResponse = AdminRouletteOperationsResponse;
const lifecycleWithClosed = z.enum(["active", "closed", "draining", "retired"]);
const environmentSummary = casinoEnvironmentSummary.extend({
  tables: z.array(adminTableInstance.extend({ lifecycle: lifecycleWithClosed.optional() })),
});
const environmentDetail = environmentSummary.extend({ drain: casinoEnvironmentDrainStatus });
const environmentPage = z.object({
  environments: z.array(environmentSummary),
  nextBefore: z.uuid().nullable(),
});
export type EnvironmentDetail = z.infer<typeof environmentDetail>;
export type EnvironmentPage = z.infer<typeof environmentPage>;
const adminKycResponse = z.object({
  verification: z.object({
    applicantId: z.string().nullable(),
    lastEventAt: z.string().datetime().nullable(),
    levelName: z.string().nullable(),
    provider: z.literal("sumsub"),
    rejectLabels: z.array(z.string()),
    reviewAnswer: z.enum(["GREEN", "RED"]).nullable(),
    reviewedAt: z.string().datetime().nullable(),
    reviewRejectType: z.enum(["FINAL", "RETRY"]).nullable(),
    status: z.enum(["not_started", "in_progress", "pending", "on_hold", "approved", "rejected"]),
  }),
});
export type AdminKycVerification = z.infer<typeof adminKycResponse>["verification"];

/**
 * An error the panel can show, carrying the code the server chose.
 *
 * The code and not the message is what the UI branches on: messages are written for people and
 * change freely, codes are the contract (D-045).
 */
export class ApiError extends Error {
  readonly code: string;
  readonly correlationId: string | undefined;
  readonly status: number;

  constructor(status: number, code: string, message: string, correlationId?: string) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.correlationId = correlationId;
    this.status = status;
  }
}

export const SESSION_EXPIRED_EVENT = "utopia:session-expired";
export const REAUTH_REQUIRED_EVENT = "utopia:reauth-required";
export const API_ERROR_EVENT = "utopia:api-error";

interface RequestOptions {
  body?: unknown;
  headers?: Record<string, string>;
  method?: "GET" | "POST" | "PUT";
  reportError?: boolean;
  signal?: AbortSignal;
}

/**
 * The only schema surface the transport needs.
 *
 * Keeping this boundary structural avoids coupling the client to a particular physical Zod
 * installation. That matters for local contract packages, where comparing two otherwise
 * compatible Zod schema internals can make TypeScript recursively expand the complete response
 * graph. Runtime validation remains unchanged: every response still passes through the supplied
 * contract schema's `safeParse` implementation.
 */
interface RuntimeResponseSchema<T> {
  safeParse(input: unknown): { data: T; success: true } | { success: false };
}

/**
 * One request, with the session cookie attached and the response validated.
 *
 * `credentials: "include"` is what lets the operator's cookie travel to another origin at all, and
 * it is why the server names this origin explicitly rather than answering with a wildcard.
 *
 * Every response is parsed with the published schema rather than cast. A cast is a promise the
 * compiler believes and nobody checks; a parse fails loudly on the first field the API renamed —
 * here, rather than three screens later as a column that is quietly empty.
 */
async function request<T>(
  path: string,
  schema: RuntimeResponseSchema<T>,
  options: RequestOptions = {},
): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    credentials: "include",
    headers: {
      ...(options.body === undefined ? {} : { "content-type": "application/json" }),
      ...options.headers,
    },
    method: options.method ?? "GET",
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const envelope = (payload as { error?: Record<string, unknown> } | null)?.error;
    const code = typeof envelope?.code === "string" ? envelope.code : "INTERNAL.ERROR";
    if (response.status === 401) {
      if (code === "AUTH.REAUTH_REQUIRED") {
        window.dispatchEvent(new Event(REAUTH_REQUIRED_EVENT));
      } else if (code !== "AUTH.INVALID_CREDENTIALS") {
        // A malformed or newly introduced 401 must fail closed. The only 401 that represents an
        // interactive attempt rather than a lost session is an incorrect login/reauthentication.
        window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      }
    }
    const error = new ApiError(
      response.status,
      code,
      typeof envelope?.message === "string" ? envelope.message : "Something went wrong.",
      typeof envelope?.correlationId === "string" ? envelope.correlationId : undefined,
    );
    const authFlowError = response.status === 401;
    if (error.correlationId && !authFlowError && options.reportError !== false) {
      window.dispatchEvent(new CustomEvent(API_ERROR_EVENT, { detail: error }));
    }
    throw error;
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    // The API and the contract have drifted. Saying so plainly beats rendering half a screen and
    // leaving somebody to work out which field is lying.
    throw new ApiError(
      response.status,
      "INTERNAL.ERROR",
      `The server sent a response this panel does not understand (${path}).`,
    );
  }

  return parsed.data;
}

export const api = {
  chatQueue: (signal?: AbortSignal) =>
    request("/v1/admin/chat/queue", chatQueue, signal ? { signal } : {}),
  chatDetail: (id: string, signal?: AbortSignal) =>
    request(
      `/v1/admin/chat/messages/${encodeURIComponent(id)}`,
      chatDetail,
      signal ? { signal } : {},
    ),
  chatDecision: (
    id: string,
    body: {
      requestId: string;
      version: number;
      verdict: "clear" | "violation";
      category: string;
      reason: string;
    },
    signal?: AbortSignal,
  ) =>
    request(
      `/v1/admin/chat/messages/${encodeURIComponent(id)}/decision`,
      z.object({ id: z.string().uuid() }),
      { method: "POST", body, ...(signal ? { signal } : {}) },
    ),
  chatAction: (
    id: string,
    body: {
      requestId: string;
      action: "mute" | "unmute" | "remove";
      reason: string;
      minutes?: number;
    },
    signal?: AbortSignal,
  ) =>
    request(
      `/v1/admin/chat/messages/${encodeURIComponent(id)}/action`,
      z.object({ id: z.string().uuid() }),
      { method: "POST", body, ...(signal ? { signal } : {}) },
    ),
  operators: (signal?: AbortSignal) =>
    request("/v1/admin/operators", operatorPage, signal ? { signal } : {}),
  moderatorGrant: (playerId: string, signal?: AbortSignal) =>
    request(
      `/v1/admin/chat/moderators/${encodeURIComponent(playerId)}`,
      moderatorGrant,
      signal ? { signal } : {},
    ),
  setModeratorGrant: (
    playerId: string,
    input: { days: number; enabled: boolean; reason: string },
  ) =>
    request(
      `/v1/admin/chat/moderators/${encodeURIComponent(playerId)}`,
      z.object({ ok: z.literal(true) }),
      { body: input, method: "PUT" },
    ),
  sbaiStatus: () =>
    request(
      "/v1/admin/sbai/status",
      z.object({
        locked: z.boolean(),
        candidateId: z.string().nullable(),
        configurationSha256: z.string().nullable(),
      }),
      { reportError: false },
    ),
  rooms: (signal?: AbortSignal, polling = false) =>
    request(
      `/v1/admin/rooms${polling ? "?poll=1" : ""}`,
      roomOperationsResponse,
      signal ? { signal } : {},
    ),

  environments: (
    options: {
      before?: string;
      includeRetired?: boolean;
      polling?: boolean;
      signal?: AbortSignal;
    } = {},
  ) => {
    const query = new URLSearchParams();
    if (options.before) query.set("before", options.before);
    if (options.includeRetired) query.set("includeRetired", "1");
    if (options.polling) query.set("poll", "1");
    return request(
      `/v1/admin/environments${query.size ? `?${query}` : ""}`,
      environmentPage,
      options.signal ? { signal: options.signal } : {},
    );
  },

  environment: (environmentInstanceId: string, signal?: AbortSignal) =>
    request(
      `/v1/admin/environments/${environmentInstanceId}`,
      environmentDetail,
      signal ? { signal } : {},
    ),

  drainEnvironment: async (environmentInstanceId: string, justification: string) =>
    request(`/v1/admin/environments/${environmentInstanceId}/drain`, environmentDetail, {
      body: casinoEnvironmentLifecycleRequest.parse({ justification }),
      method: "POST",
      reportError: false,
    }),

  retireEnvironment: async (environmentInstanceId: string, justification: string) =>
    request(`/v1/admin/environments/${environmentInstanceId}/retire`, environmentDetail, {
      body: casinoEnvironmentLifecycleRequest.parse({ justification }),
      method: "POST",
      reportError: false,
    }),

  closeTable: async (environmentInstanceId: string, tableId: string, justification: string) =>
    request(
      `/v1/admin/environments/${environmentInstanceId}/tables/${encodeURIComponent(tableId)}/close`,
      environmentDetail,
      {
        body: casinoEnvironmentLifecycleRequest.parse({ justification }),
        method: "POST",
        reportError: false,
      },
    ),

  openTable: async (environmentInstanceId: string, tableId: string, justification: string) =>
    request(
      `/v1/admin/environments/${environmentInstanceId}/tables/${encodeURIComponent(tableId)}/open`,
      environmentDetail,
      {
        body: casinoEnvironmentLifecycleRequest.parse({ justification }),
        method: "POST",
        reportError: false,
      },
    ),

  operatorProfiles: () =>
    request("/v1/admin/config-profiles", operatorProfileLibrary, { reportError: false }),
  saveOperatorProfile: (input: z.input<typeof operatorProfileWriteRequest>) =>
    request("/v1/admin/config-profiles/profile", z.unknown(), {
      method: "POST",
      body: operatorProfileWriteRequest.parse(input),
    }),
  saveOperatorPolicy: (input: z.input<typeof operatorPolicyWriteRequest>) =>
    request("/v1/admin/config-profiles/policy", z.unknown(), {
      method: "POST",
      body: operatorPolicyWriteRequest.parse(input),
    }),
  archiveOperatorProfile: (input: z.input<typeof operatorProfileArchiveRequest>) =>
    request("/v1/admin/config-profiles/archive", z.unknown(), {
      method: "POST",
      body: operatorProfileArchiveRequest.parse(input),
    }),
  waitlist: (filters: WaitlistFilters, signal?: AbortSignal) => {
    const query = new URLSearchParams({ status: filters.status });
    if (filters.before) query.set("before", filters.before);
    return request(`/v1/admin/waitlist?${query}`, waitlistPage, {
      ...(signal ? { signal } : {}),
      reportError: false,
    });
  },
  reviewWaitlist: (id: string, reviewed: boolean) =>
    request(`/v1/admin/waitlist/${encodeURIComponent(id)}/review`, waitlistReview, {
      method: "POST",
      body: { reviewed },
      reportError: false,
    }),
  feedback: (filters: FeedbackFilters, signal?: AbortSignal) => {
    const query = new URLSearchParams({ status: filters.status });
    if (filters.category) query.set("category", filters.category);
    if (filters.search) query.set("search", filters.search);
    if (filters.before) query.set("before", filters.before);
    return request(`/v1/admin/feedback?${query}`, feedbackPage, {
      ...(signal ? { signal } : {}),
      reportError: false,
    });
  },
  feedbackSummary: (signal?: AbortSignal) =>
    request("/v1/admin/feedback/summary", feedbackSummary, {
      ...(signal ? { signal } : {}),
      reportError: false,
    }),
  reviewFeedback: (id: string, reviewed: boolean) =>
    request(`/v1/admin/feedback/${encodeURIComponent(id)}/review`, feedbackReview, {
      method: "POST",
      body: { reviewed },
      reportError: false,
    }),
  replyToFeedback: (id: string, message: string) =>
    request(`/v1/admin/feedback/${encodeURIComponent(id)}/reply`, feedbackReply, {
      method: "POST",
      body: { message },
      reportError: false,
    }),
  blackjackOperations: (signal?: AbortSignal, polling = false) =>
    request(
      `/v1/admin/blackjack/operations${polling ? "?poll=1" : ""}`,
      adminBlackjackOperationsResponse,
      signal ? { signal } : {},
    ),
  activateBlackjackConfigProfile: (input: AdminConfigProfileActivationRequest) =>
    request("/v1/admin/blackjack/config-profile", adminConfigProfileActivationResponse, {
      body: adminConfigProfileActivationRequest.parse(input),
      method: "POST",
      reportError: false,
    }),
  cancelBlackjackConfigProfile: (input: AdminConfigProfileCancellationRequest) =>
    request("/v1/admin/blackjack/config-profile/cancel", adminConfigProfileCancellationResponse, {
      body: adminConfigProfileCancellationRequest.parse(input),
      method: "POST",
      reportError: false,
    }),
  pokerOperations: (signal?: AbortSignal, polling = false) =>
    request(
      `/v1/admin/poker/operations${polling ? "?poll=1" : ""}`,
      adminPokerOperationsResponse,
      signal ? { signal } : {},
    ),
  activatePokerConfigProfile: (input: AdminConfigProfileActivationRequest) =>
    request("/v1/admin/poker/config-profile", adminConfigProfileActivationResponse, {
      body: adminConfigProfileActivationRequest.parse(input),
      method: "POST",
      reportError: false,
    }),
  cancelPokerConfigProfile: (input: AdminConfigProfileCancellationRequest) =>
    request("/v1/admin/poker/config-profile/cancel", adminConfigProfileCancellationResponse, {
      body: adminConfigProfileCancellationRequest.parse(input),
      method: "POST",
      reportError: false,
    }),
  rouletteOperations: (signal?: AbortSignal, polling = false) =>
    request(
      `/v1/admin/roulette/operations${polling ? "?poll=1" : ""}`,
      adminRouletteOperationsResponse,
      signal ? { signal } : {},
    ),
  activateRouletteConfigProfile: (input: AdminConfigProfileActivationRequest) =>
    request("/v1/admin/roulette/config-profile", adminConfigProfileActivationResponse, {
      body: adminConfigProfileActivationRequest.parse(input),
      method: "POST",
      reportError: false,
    }),
  cancelRouletteConfigProfile: (input: AdminConfigProfileCancellationRequest) =>
    request("/v1/admin/roulette/config-profile/cancel", adminConfigProfileCancellationResponse, {
      body: adminConfigProfileCancellationRequest.parse(input),
      method: "POST",
      reportError: false,
    }),

  casinoProfit: (
    options: { currency?: string; from?: string; game?: string; table?: string; to?: string } = {},
  ) => {
    const query = new URLSearchParams();
    for (const key of ["currency", "from", "game", "table", "to"] as const) {
      const value = options[key];
      if (value) query.set(key, value);
    }
    return request(
      `/v1/admin/reports/casino-profit${query.size ? `?${query}` : ""}`,
      casinoProfitResponse,
    );
  },

  rounds: (
    options: {
      before?: string;
      from?: string;
      game?: string;
      player?: string;
      roundId?: string;
      signal?: AbortSignal;
      status?: string;
      table?: string;
      to?: string;
    } = {},
  ) => {
    const query = new URLSearchParams();
    for (const key of [
      "before",
      "from",
      "game",
      "player",
      "roundId",
      "status",
      "table",
      "to",
    ] as const) {
      const value = options[key];
      if (value) query.set(key, value);
    }
    return request(
      `/v1/admin/rounds${query.size ? `?${query}` : ""}`,
      adminRoundsResponse,
      options.signal ? { signal: options.signal } : {},
    );
  },

  round: (id: string, signal?: AbortSignal) =>
    request(`/v1/admin/rounds/${id}`, adminRoundDetailResponse, signal ? { signal } : {}),

  rngChain: (signal?: AbortSignal) =>
    request("/v1/admin/rng-chain", rngChainVerification, signal ? { signal } : {}),

  transactions: (
    options: {
      before?: string;
      currency?: string;
      from?: string;
      kind?: string;
      maxAmount?: string;
      minAmount?: string;
      player?: string;
      reference?: string;
      signal?: AbortSignal;
      to?: string;
      transactionId?: string;
    } = {},
  ) => {
    const query = new URLSearchParams();
    for (const key of [
      "before",
      "currency",
      "from",
      "kind",
      "maxAmount",
      "minAmount",
      "player",
      "reference",
      "to",
      "transactionId",
    ] as const) {
      const value = options[key];
      if (value) query.set(key, value);
    }
    return request(
      `/v1/admin/transactions${query.size ? `?${query}` : ""}`,
      transactionExplorerResponse,
      options.signal ? { signal: options.signal } : {},
    );
  },

  transaction: (id: string, signal?: AbortSignal) =>
    request(`/v1/admin/transactions/${id}`, transactionExplorerDetail, signal ? { signal } : {}),

  royalSpinCampaigns: (signal?: AbortSignal) =>
    request(
      "/v1/admin/royal-spin/campaigns",
      royalSpinCampaignListResponse,
      signal ? { signal } : {},
    ),

  royalSpinCampaign: (id: string, signal?: AbortSignal) =>
    request(
      `/v1/admin/royal-spin/campaigns/${id}`,
      royalSpinCampaignDetailResponse,
      signal ? { signal } : {},
    ),

  createRoyalSpinCampaign: async (input: unknown) =>
    request("/v1/admin/royal-spin/campaigns", royalSpinCampaignVersion, {
      body: createRoyalSpinCampaignRequest.parse(input),
      method: "POST",
    }),

  submitRoyalSpinCampaign: async (id: string, justification: string) =>
    request(`/v1/admin/royal-spin/campaigns/${id}/submit`, royalSpinCampaignLifecycleResponse, {
      body: royalSpinCampaignDecisionRequest.parse({ justification }),
      method: "POST",
    }),

  approveRoyalSpinCampaign: async (id: string, justification: string) =>
    request(`/v1/admin/royal-spin/campaigns/${id}/approve`, royalSpinCampaignLifecycleResponse, {
      body: royalSpinCampaignDecisionRequest.parse({ justification }),
      method: "POST",
    }),

  setRoyalSpinKillSwitch: async (enabled: boolean, justification: string) =>
    request("/v1/admin/royal-spin/kill-switch", royalSpinKillSwitchResponse, {
      body: royalSpinKillSwitchRequest.parse({ enabled, justification }),
      method: "POST",
    }),
  /**
   * Approve somebody else's request. The money moves in the same call (D-018).
   *
   * The justification is the approver's own, not the raiser's: two people looked at this and the
   * record should say what each of them thought.
   */
  approveAction: async (actionId: string, justification: string) =>
    request(`/v1/admin/actions/${actionId}/approve`, operatorApprovalResponse, {
      body: { justification },
      method: "POST",
    }),

  /**
   * A page of the operator audit log, narrowed by whatever was asked for.
   *
   * Empty filters are dropped rather than sent blank: `?actionType=` is a filter on the empty
   * string, and the API would rightly find nothing.
   *
   * `verified` comes back whatever the filter, because the chain is a property of the whole log —
   * one that could be made to look intact by narrowing would hide the thing this screen exists to
   * show.
   */
  audit: (
    options: {
      actionType?: string;
      actorId?: string;
      before?: string;
      from?: string;
      signal?: AbortSignal;
      targetId?: string;
      to?: string;
    } = {},
  ) => {
    const query = new URLSearchParams();
    for (const key of ["actionType", "actorId", "before", "from", "targetId", "to"] as const) {
      const value = options[key];
      if (value) query.set(key, value);
    }

    const suffix = query.size > 0 ? `?${query.toString()}` : "";
    return request(
      `/v1/admin/audit${suffix}`,
      adminAuditResponse,
      options.signal ? { signal: options.signal } : {},
    );
  },

  /** What is waiting for a decision. Expired requests are filtered out by the API. */
  pendingActions: (signal?: AbortSignal) =>
    request("/v1/admin/actions", pendingOperatorActionsResponse, signal ? { signal } : {}),

  /**
   * `async` so a rejected input arrives the same way a rejected request does — `parse` throws
   * synchronously, and a caller should not have to guard twice.
   */
  raiseAction: async (input: AdminActionRequest | OperatorActionRequest) =>
    request("/v1/admin/actions", raisedActionResponse, {
      body: operatorActionRequest.parse(input),
      headers: { "idempotency-key": crypto.randomUUID() },
      method: "POST",
    }),

  /** Turn a request down. The reason is what the raiser needs and the request cannot say. */
  rejectAction: async (actionId: string, justification: string) =>
    request(`/v1/admin/actions/${actionId}/reject`, rejectedActionResponse, {
      body: adminActionDecision.parse({ justification }),
      method: "POST",
    }),

  forceLogout: (playerId: string, justification: string) =>
    request(`/v1/admin/players/${playerId}/sessions/revoke`, forceLogoutResponse, {
      body: { justification },
      method: "POST",
    }),

  /**
   * `async` so a rejected input arrives the same way a rejected request does.
   *
   * `parse` throws synchronously, and a function that sometimes throws before returning a promise
   * and sometimes rejects one is a function every caller has to guard twice. One shape, one catch.
   */
  login: async (credentials: { code: string; email: string; password: string }) =>
    request("/v1/admin/auth/login", adminSessionResponse, {
      // Validated before it leaves, so an obviously malformed code costs no round trip and none of
      // the operator's rate-limit budget. The server applies the same rule regardless.
      body: adminLoginRequest.parse(credentials),
      method: "POST",
    }),

  reauth: async (credentials: { code: string; password: string }) =>
    request("/v1/admin/auth/reauth", reauthResponse, {
      body: adminReauthRequest.parse(credentials),
      method: "POST",
    }),

  me: (signal?: AbortSignal) =>
    request("/v1/admin/auth/me", adminSessionResponse, signal ? { signal } : {}),

  caseQueue: (options: Partial<AdminCaseQueueQuery> & { signal?: AbortSignal } = {}) => {
    const query = new URLSearchParams();
    if (options.before) query.set("before", options.before);
    if (options.limit) query.set("limit", String(options.limit));
    if (options.overdue) query.set("overdue", "true");
    if (options.priority) query.set("priority", options.priority);
    if (options.scope) query.set("scope", options.scope);
    if (options.status) query.set("status", options.status);
    if (options.type) query.set("type", options.type);
    return request(
      `/v1/admin/cases${query.size ? `?${query}` : ""}`,
      adminCaseQueueResponse,
      options.signal ? { signal: options.signal } : {},
    );
  },

  player: (playerId: string, signal?: AbortSignal) =>
    request(`/v1/admin/players/${playerId}`, playerOverviewResponse, signal ? { signal } : {}),

  playerKyc: (playerId: string, signal?: AbortSignal) =>
    request(`/v1/admin/players/${playerId}/kyc`, adminKycResponse, signal ? { signal } : {}),

  addPlayerCaseNote: async (playerId: string, caseId: string, body: string) =>
    request(`/v1/admin/players/${playerId}/cases/${caseId}/notes`, playerCaseResponse, {
      body: addPlayerCaseNoteRequest.parse({ body }),
      method: "POST",
    }),

  createPlayerCase: async (playerId: string, input: CreatePlayerCaseRequest) =>
    request(`/v1/admin/players/${playerId}/cases`, playerCaseResponse, {
      body: createPlayerCaseRequest.parse(input),
      method: "POST",
    }),

  playerCases: (playerId: string, signal?: AbortSignal) =>
    request(`/v1/admin/players/${playerId}/cases`, playerCasesResponse, signal ? { signal } : {}),

  /**
   * A page of the player's ledger. `before` is a cursor from the previous page, never a row the
   * caller picked — the ledger is written to while it is read, and an offset would skip or repeat
   * entries as new ones land above the page.
   */
  playerLedger: (playerId: string, options: { before?: string; signal?: AbortSignal } = {}) => {
    const query = options.before ? `?before=${encodeURIComponent(options.before)}` : "";
    return request(
      `/v1/admin/players/${playerId}/ledger${query}`,
      playerLedgerResponse,
      options.signal ? { signal: options.signal } : {},
    );
  },

  playerGameRound: (playerId: string, roundId: string, signal?: AbortSignal) =>
    request(
      `/v1/admin/players/${playerId}/games/${roundId}`,
      playerGameRoundDetailResponse,
      signal ? { signal } : {},
    ),

  playerGameRounds: (playerId: string, signal?: AbortSignal) =>
    request(
      `/v1/admin/players/${playerId}/games`,
      playerGameRoundsResponse,
      signal ? { signal } : {},
    ),

  playerSessions: (playerId: string, signal?: AbortSignal) =>
    request(
      `/v1/admin/players/${playerId}/sessions`,
      playerSessionsResponse,
      signal ? { signal } : {},
    ),

  playerResponsibleGaming: (playerId: string, signal?: AbortSignal) =>
    request(
      `/v1/admin/players/${playerId}/responsible-gaming`,
      playerResponsibleGamingResponse,
      signal ? { signal } : {},
    ),

  searchPlayers: (term: string, options: { before?: string; signal?: AbortSignal } = {}) => {
    const query = new URLSearchParams();
    if (term) query.set("q", term);
    if (options.before) query.set("before", options.before);
    return request(
      `/v1/admin/players${query.size ? `?${query}` : ""}`,
      playerSearchResponse,
      options.signal ? { signal: options.signal } : {},
    );
  },

  /** `async` for the same reason as `login`: one failure shape for the caller to catch. */
  setStatus: async (playerId: string, change: { reason: string; status: string }) =>
    request(`/v1/admin/players/${playerId}/status`, adminStatusChangeResponse, {
      body: adminStatusChangeRequest.parse(change),
      method: "POST",
    }),

  updatePlayerCase: async (playerId: string, caseId: string, input: UpdatePlayerCaseRequest) =>
    request(`/v1/admin/players/${playerId}/cases/${caseId}`, playerCaseResponse, {
      body: updatePlayerCaseRequest.parse(input),
      method: "POST",
    }),

  signOut: () => request("/v1/admin/auth/logout", signOutResponse, { method: "POST" }),
};
